import { LineChart, LineChartProps } from '@mui/x-charts/LineChart';
import { COLORS } from '../../../../utils/colors';
import { CreditUtilizationPoint } from './chartTypes';

interface CreditUtilizationChartProps {
  data: CreditUtilizationPoint[];
}

const getResponsiveConfig = (): Partial<LineChartProps> => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return {
    height: isMobile ? 280 : 320,
    margin: {
      left: 0,
      right: isMobile ? 10 : 16,
      top: 16,
      bottom: isMobile ? 60 : 48,
    },
  };
};

function CreditUtilizationChart({ data }: CreditUtilizationChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        No credit utilization data available
      </div>
    );
  }

  const chartData = data.map((point) => ({
    month: point.month.split('-')[1],
    utilizationPercent: point.utilizationPercent,
  }));

  return (
    <LineChart
      dataset={chartData}
      xAxis={[
        {
          dataKey: 'month',
          scaleType: 'point',
          tickLabelStyle: {
            fontSize: 11,
            fill: COLORS.TEXT_SECONDARY,
            fontFamily: 'Inter',
          },
          disableLine: true,
          disableTicks: true,
        },
      ]}
      series={[
        {
          dataKey: 'utilizationPercent',
          label: 'Utilization %',
          color: COLORS.SUCCESS_600,
          area: true,
          curve: 'monotoneX',
          showMark: true,
          valueFormatter: (value: number | null) => {
            return `${(value || 0).toFixed(1)}%`;
          },
        },
      ]}
      yAxis={[
        {
          width: 36,
          tickLabelStyle: {
            fontSize: 10,
            fill: COLORS.TEXT_SECONDARY,
            fontFamily: 'Inter',
          },
          disableLine: true,
          disableTicks: true,
          min: 0,
          max: 100,
          valueFormatter: (value: number) => {
            return `${value}%`;
          },
        },
      ]}
      grid={{ horizontal: true, vertical: false }}
      sx={{
        '& .MuiLineElement-root': {
          strokeWidth: 2.5,
        },
        '& .MuiAreaElement-root': {
          fill: COLORS.SUCCESS_600,
          fillOpacity: 0.12,
        },
        '& .MuiMarkElement-root': {
          stroke: COLORS.SUCCESS_600,
          strokeWidth: 2,
          fill: '#FFFFFF',
          r: 4,
        },
        '& .MuiChartsGrid-line': {
          stroke: COLORS.GREY_100,
          strokeDasharray: '3 3',
        },
      }}
      {...getResponsiveConfig()}
    />
  );
}

export default CreditUtilizationChart;
